import { inject, Injectable } from '@angular/core';
import { Observable, Observer } from 'rxjs';
import { HttpGatewayService } from '../core/services/http-gateway.service';
import { Department } from '../shared/types/department';
import { UserOnboarding } from '../shared/types/user';
import { OrganizationType, OrganizationWithUserCreationResponse } from '../shared/types/organization';
import { CurrencyContract, CurrencyInfo } from '../shared/types/currency';
import { decodeHtml } from '../shared/utils/html-utils';
import { ApplicationStore, ApplicationStoreType } from '../core/store/application.store';

const ONBOARDING_URLS = {
    CREATE_ONBOARDING_EMAIL: 'registration/v5/onboarding/create-email',
    CONFIRM_ONBOARDING_EMAIL: 'registration/v5/onboarding/verify-email',
    CREATE_ORGANIZATION_WITH_USER: 'registration/v5/onboarding/create-user-with-org',
    GET_ORGANIZATION_TYPES: 'registration/v5/onboarding/get-org-types',
    GET_CURRENCIES: 'registration/v5/onboarding/get-currencies',
};

@Injectable({ providedIn: 'root' })
export class OnboardingService {
    private http = inject(HttpGatewayService);
    private applicationStore: ApplicationStoreType = inject(ApplicationStore);

    public createEmailVerification(firstName: string, lastName: string, email: string): Observable<UserOnboarding> {
        return this.http.post(ONBOARDING_URLS.CREATE_ONBOARDING_EMAIL, { firstName, lastName, email });
    }

    public verifyEmail(email: string, token: string): Observable<{ success: boolean }> {
        return this.http.post(ONBOARDING_URLS.CONFIRM_ONBOARDING_EMAIL, { token, email });
    }

    public createOrganizationWithUser(
        email: string,
        password: string,
        firstName: string,
        lastName: string,
        department: Department,
        orgName: string,
        orgTypeCode: string,
        currency: string,
    ): Observable<OrganizationWithUserCreationResponse> {
        const request = {
            email,
            password,
            firstName,
            lastName,
            department,
            orgName,
            currency,
            orgTypeCode,
        };

        return this.http.post(ONBOARDING_URLS.CREATE_ORGANIZATION_WITH_USER, request);
    }

    public getOrganizationTypes(): Observable<OrganizationType[]> {
        return new Observable<OrganizationType[]>((observer: Observer<OrganizationType[]>) => {
            const availableTypes = this.applicationStore.organizationTypes();

            if (!availableTypes || availableTypes.length === 0) {
                this.http.get<OrganizationType[]>(ONBOARDING_URLS.GET_ORGANIZATION_TYPES).subscribe({
                    next: (response: OrganizationType[]) => {
                        this.applicationStore.setOrganizationTypes(response);

                        observer.next(response);
                        observer.complete();
                    },
                    error: (error) => {
                        observer.next([]);
                        observer.complete();
                    },
                });
            } else {
                observer.next(availableTypes);
                observer.complete();
            }
        });
    }

    public getCurrencies(): Observable<CurrencyInfo[]> {
        return new Observable<CurrencyInfo[]>((observer: Observer<CurrencyInfo[]>) => {
            const availableCurrencies = this.applicationStore.currencies();

            if (!availableCurrencies || availableCurrencies.length === 0) {
                this.http.get<CurrencyContract[]>(ONBOARDING_URLS.GET_CURRENCIES).subscribe({
                    next: (contracts) => {
                        const currencies = contracts.map((c) => new CurrencyInfo(c.code, decodeHtml(c.html)));
                        this.applicationStore.setCurrencies(currencies);
                        observer.next(currencies);
                        observer.complete();
                    },
                    error: () => {
                        observer.next([]);
                        observer.complete();
                    },
                });
            } else {
                observer.next(availableCurrencies);
                observer.complete();
            }
        });
    }
}
